import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface WebAuthnCredentialOptions {
    challenge: string;
    rp: { id: string; name: string };
    user: { id: string; name: string; displayName: string };
    pubKeyCredParams: { type: string; alg: number }[];
    timeout?: number;
    attestation?: string;
    authenticatorSelection?: {
        authenticatorAttachment?: string;
        requireResidentKey?: boolean;
        userVerification?: string;
    };
    excludeCredentials?: { id: string; type: string }[];
}

export interface WebAuthnAssertionOptions {
    challenge: string;
    timeout?: number;
    rpId?: string;
    allowCredentials?: { id: string; type: string; transports?: string[] }[];
    userVerification?: string;
}

export interface AuthenticatorQRCodeResponse {
    success: boolean;
    message?: string;
    image?: string;
    secret?: string;
}

export interface RegisteredCredential {
    id: number;
    credentialId: string;
    publicKey: string;
    signCount: number;
    credType: string;
    regDate: Date;
    aaguid: string;
    descriptorJson?: string;
}

export interface UserWebauthnCredentialDTO {
    id: number;
    user_id: number;
    CredType: string;
    Name: string | null;
    RegDate: string;  // ISO date string
    AaGuid: string;
    SignatureCounter: number;
}

@Injectable({
    providedIn: 'root'
})
export class TwoFactorAuthService {
    private readonly http = inject(HttpClient);
    private readonly apiUrl = environment.config.api_url;

    // ============ WebAuthn Methods ============

    /**
     * Check if WebAuthn is enabled on the server
     */
    getWebAuthnStatus(): Observable<boolean> {
        return this.http.get<boolean>(`${this.apiUrl}Auth/WebAuthn/status`);
    }

    /**
     * Get credential creation options for WebAuthn registration
     */
    getCredentialOptions(token?: string): Observable<WebAuthnCredentialOptions> {
        const url = token
            ? `${this.apiUrl}Auth/WebAuthn/MakeCredentialOptions/${token}`
            : `${this.apiUrl}Auth/WebAuthn/MakeCredentialOptions`;
        return this.http.post<WebAuthnCredentialOptions>(url, {});
    }

    /**
     * Register a new WebAuthn credential
     */
    registerCredential(attestationResponse: any, token?: string, name?: string): Observable<RegisteredCredential> {
        let url = token
            ? `${this.apiUrl}Auth/WebAuthn/MakeCredential/${token}`
            : `${this.apiUrl}Auth/WebAuthn/MakeCredential`;
        if (name) {
            url += `?name=${encodeURIComponent(name)}`;
        }
        return this.http.post<RegisteredCredential>(url, attestationResponse);
    }

    /**
     * Get assertion options for WebAuthn login
     */
    getAssertionOptions(): Observable<WebAuthnAssertionOptions> {
        return this.http.post<WebAuthnAssertionOptions>(`${this.apiUrl}Auth/WebAuthn/AssertionOptions`, {});
    }

    /**
     * Make WebAuthn assertion (login)
     */
    makeAssertion(assertionResponse: any): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}Auth/WebAuthn/MakeAssertionNew`, assertionResponse);
    }

    /**
     * Delete a WebAuthn credential
     */
    deleteCredential(credentialId: number): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}Auth/WebAuthn/Credential/${credentialId}`);
    }

    /**
     * Get all WebAuthn credentials registered by the current user
     */
    getCredentials(): Observable<UserWebauthnCredentialDTO[]> {
        return this.http.get<UserWebauthnCredentialDTO[]>(`${this.apiUrl}Auth/WebAuthn/Credentials`);
    }

    // ============ YubiKey Methods ============

    /**
     * Register a YubiKey device
     * @param code The 44-character OTP from the YubiKey
     * @param token Registration token (optional, for self-registration)
     */
    registerYubiKey(code: string, token: string): Observable<{ success: boolean; message?: string }> {
        return this.http.post<{ success: boolean; message?: string }>(
            `${this.apiUrl}Auth/YubicoOTP/Register/${code}/${token}`,
            {}
        );
    }

    /**
     * Validate a YubiKey OTP
     */
    validateYubiKey(code: string): Observable<{ success: boolean }> {
        return this.http.post<{ success: boolean }>(`${this.apiUrl}Auth/validate/YubicoOTP`, { code });
    }

    // ============ Authenticator (TOTP) Methods ============

    /**
     * Get QR code for authenticator app setup
     * @param token Registration token
     */
    getAuthenticatorQRCode(token: string): Observable<AuthenticatorQRCodeResponse> {
        return this.http.get<AuthenticatorQRCodeResponse>(`${this.apiUrl}Auth/AuthenticatorQRCode/${token}`);
    }

    /**
     * Verify authenticator code during registration
     * @param code The 6-digit TOTP code
     * @param token Registration token
     */
    verifyAuthenticator(code: string, token: string): Observable<boolean> {
        return this.http.post<boolean>(`${this.apiUrl}Auth/Authenticator/Verify/${code}/${token}`, {});
    }

    /**
     * Validate authenticator code during login
     */
    validateAuthenticator(code: string): Observable<{ success: boolean }> {
        return this.http.post<{ success: boolean }>(`${this.apiUrl}Auth/validate/Authenticator`, { code });
    }

    // ============ WebAuthn Browser API Helpers ============

    private static readonly PASSKEY_FLAG_KEY = 'stela_webauthn_registered';

    /**
     * Mark that a passkey has been registered or successfully used on this device.
     * Used to decide whether to show the WebAuthn login button.
     */
    markPasskeyRegistered(): void {
        localStorage.setItem(TwoFactorAuthService.PASSKEY_FLAG_KEY, '1');
    }

    /**
     * Returns true if a passkey has previously been registered or used on this device.
     */
    hasRegisteredPasskey(): boolean {
        return this.isWebAuthnSupported() &&
            localStorage.getItem(TwoFactorAuthService.PASSKEY_FLAG_KEY) === '1';
    }

    /**
     * Check if WebAuthn is supported by the browser
     */
    isWebAuthnSupported(): boolean {
        return !!(window.PublicKeyCredential);
    }

    /**
     * Check if the browser has any discoverable passkeys for the current origin.
     * Uses mediation:'silent' — no UI is shown and no result is sent to the server.
     * Returns false when definitely no credentials exist; true otherwise (including on error).
     */
    async hasDiscoverableCredentials(): Promise<boolean> {
        if (!this.isWebAuthnSupported()) return false;
        try {
            const result = await navigator.credentials.get({
                publicKey: {
                    challenge: crypto.getRandomValues(new Uint8Array(16)),
                    userVerification: 'discouraged',
                    timeout: 5000
                },
                mediation: 'silent' as CredentialMediationRequirement
            } as CredentialRequestOptions);
            return result !== null;
        } catch {
            // Not supported or unknown error — show the button to be safe
            return true;
        }
    }

    /**
     * Check if platform authenticator (Touch ID, Face ID, Windows Hello) is available
     */
    async isPlatformAuthenticatorAvailable(): Promise<boolean> {
        if (!this.isWebAuthnSupported()) return false;
        try {
            return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        } catch {
            return false;
        }
    }

    /**
     * Create a credential using the browser's WebAuthn API
     */
    async createCredential(options: WebAuthnCredentialOptions): Promise<PublicKeyCredential | null> {
        if (!this.isWebAuthnSupported()) {
            throw new Error('WebAuthn is not supported in this browser');
        }

        const publicKeyOptions: PublicKeyCredentialCreationOptions = {
            challenge: this.base64ToArrayBuffer(options.challenge),
            rp: options.rp,
            user: {
                id: this.base64ToArrayBuffer(options.user.id),
                name: options.user.name,
                displayName: options.user.displayName
            },
            pubKeyCredParams: options.pubKeyCredParams.map(p => ({
                type: p.type as PublicKeyCredentialType,
                alg: p.alg
            })),
            timeout: options.timeout || 60000,
            attestation: (options.attestation as AttestationConveyancePreference) || 'direct',
            authenticatorSelection: options.authenticatorSelection ? {
                authenticatorAttachment: options.authenticatorSelection.authenticatorAttachment as AuthenticatorAttachment,
                requireResidentKey: options.authenticatorSelection.requireResidentKey,
                userVerification: options.authenticatorSelection.userVerification as UserVerificationRequirement
            } : undefined,
            excludeCredentials: options.excludeCredentials?.map(c => ({
                id: this.base64ToArrayBuffer(c.id),
                type: c.type as PublicKeyCredentialType
            }))
        };

        const credential = await navigator.credentials.create({
            publicKey: publicKeyOptions
        });

        return credential as PublicKeyCredential;
    }

    /**
     * Get assertion using the browser's WebAuthn API
     */
    async getAssertion(options: WebAuthnAssertionOptions): Promise<PublicKeyCredential | null> {
        if (!this.isWebAuthnSupported()) {
            throw new Error('WebAuthn is not supported in this browser');
        }

        const publicKeyOptions: PublicKeyCredentialRequestOptions = {
            challenge: this.base64ToArrayBuffer(options.challenge),
            timeout: options.timeout || 60000,
            rpId: options.rpId,
            allowCredentials: options.allowCredentials?.map(c => ({
                id: this.base64ToArrayBuffer(c.id),
                type: c.type as PublicKeyCredentialType,
                transports: c.transports as AuthenticatorTransport[]
            })),
            userVerification: (options.userVerification as UserVerificationRequirement) || 'preferred'
        };

        const assertion = await navigator.credentials.get({
            publicKey: publicKeyOptions
        });

        return assertion as PublicKeyCredential;
    }

    /**
     * Convert credential to a format suitable for sending to the server
     */
    credentialToJSON(credential: PublicKeyCredential): any {
        const response = credential.response as AuthenticatorAttestationResponse;
        return {
            id: credential.id,
            rawId: this.arrayBufferToBase64(credential.rawId),
            type: credential.type,
            response: {
                clientDataJSON: this.arrayBufferToBase64(response.clientDataJSON),
                attestationObject: this.arrayBufferToBase64(response.attestationObject)
            }
        };
    }

    /**
     * Convert assertion to a format suitable for sending to the server
     */
    assertionToJSON(assertion: PublicKeyCredential): any {
        const response = assertion.response as AuthenticatorAssertionResponse;
        return {
            id: assertion.id,
            rawId: this.arrayBufferToBase64(assertion.rawId),
            type: assertion.type,
            response: {
                clientDataJSON: this.arrayBufferToBase64(response.clientDataJSON),
                authenticatorData: this.arrayBufferToBase64(response.authenticatorData),
                signature: this.arrayBufferToBase64(response.signature),
                userHandle: response.userHandle ? this.arrayBufferToBase64(response.userHandle) : null
            }
        };
    }

    // ============ Utility Methods ============

    private base64ToArrayBuffer(base64: string): ArrayBuffer {
        // Handle URL-safe base64
        const base64Standard = base64.replace(/-/g, '+').replace(/_/g, '/');
        const padding = '='.repeat((4 - base64Standard.length % 4) % 4);
        const binaryString = atob(base64Standard + padding);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }

    private arrayBufferToBase64(buffer: ArrayBuffer): string {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        // Use URL-safe base64
        return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    }
}
